import { type ReactNode, useEffect, useState } from 'react';
import { ErrorMsg, KV, Loading, PageHead, Section } from '../components/ui';
import { api, errorText } from '../lib/api';
import { useSession } from '../lib/session';
import type { SettingsValues, WorkerSettings } from '../lib/types';
import { AgentTokens, MirageProfileForm, ModelPolicyForm, OpenClawForm, ReportsForm } from './AgentSettings';
import { Suppressions } from './prospects/Suppressions';

function useSave(key: keyof SettingsValues) {
  const { reloadSettings } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  async function save(value: unknown) {
    setBusy(true);
    setError('');
    setOk('');
    try {
      await api(`/api/settings/${key}`, { method: 'PUT', body: { value } });
      setOk('Saved.');
      await reloadSettings();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, ok, save };
}

function ListEditor({ k, title, help, values }: { k: 'team_size_bands' | 'service_categories' | 'client_categories'; title: string; help: string; values: string[] }) {
  const [items, setItems] = useState(values);
  const [add, setAdd] = useState('');
  const { busy, error, ok, save } = useSave(k);
  useEffect(() => setItems(values), [values]);
  const dirty = items.join('\u0000') !== values.join('\u0000');
  const move = (i: number, d: -1 | 1) => {
    const n = [...items];
    const j = i + d;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]];
    setItems(n);
  };
  return (
    <Section title={title}>
      <p className="muted small">{help}</p>
      <ol className="list-editor">
        {items.map((it, i) => (
          <li key={`${it}-${i}`}>
            <span>{it}</span>
            <span className="row gap-s">
              <button type="button" className="link small" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${it} up`}>
                up
              </button>
              <button type="button" className="link small" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label={`Move ${it} down`}>
                down
              </button>
              <button type="button" className="link small" onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label={`Remove ${it}`}>
                remove
              </button>
            </span>
          </li>
        ))}
      </ol>
      <form
        className="inline-form"
        aria-label={`Add to ${title}`}
        onSubmit={(e) => {
          e.preventDefault();
          const v = add.trim();
          if (v && !items.includes(v)) setItems([...items, v]);
          setAdd('');
        }}
      >
        <input value={add} onChange={(e) => setAdd(e.target.value)} placeholder="new entry" aria-label={`New entry for ${title}`} />
        <button type="submit" className="btn small" disabled={!add.trim()}>
          Add
        </button>
      </form>
      <div className="row gap center">
        <button type="button" className="btn primary small" disabled={!dirty || busy} onClick={() => void save(items)}>
          Save
        </button>
        {dirty && (
          <button type="button" className="link small" onClick={() => setItems(values)}>
            undo changes
          </button>
        )}
        {ok && !dirty && <span className="small ok-text">{ok}</span>}
      </div>
      <ErrorMsg>{error}</ErrorMsg>
    </Section>
  );
}

function WorkerForm({ value }: { value: WorkerSettings }) {
  const [w, setW] = useState(value);
  const { busy, error, ok, save } = useSave('worker');
  useEffect(() => setW(value), [value]);
  const dirty = JSON.stringify(w) !== JSON.stringify(value);
  const q = w.quiet_hours;
  return (
    <Section title="Worker" id="worker">
      <form
        className="form-grid"
        aria-label="Worker settings"
        onSubmit={(e) => {
          e.preventDefault();
          void save(w);
        }}
      >
        <label className="field">
          <span>Concurrency (1–8 jobs at a time)</span>
          <input type="number" min={1} max={8} value={w.concurrency} onChange={(e) => setW({ ...w, concurrency: Number(e.target.value) })} />
        </label>
        <div className="field">
          <span>Paused (rendering priority)</span>
          <p className="small">{w.paused ? `yes${w.pause_reason ? ` — ${w.pause_reason}` : ''}` : 'no'} · toggle it on the Jobs page</p>
        </div>
        <label className="check block span2">
          <input type="checkbox" checked={q.enabled} onChange={(e) => setW({ ...w, quiet_hours: { ...q, enabled: e.target.checked } })} /> Quiet hours: start no
          new job between
        </label>
        <label className="field">
          <span>Start (HH:MM)</span>
          <input type="time" value={q.start} disabled={!q.enabled} onChange={(e) => setW({ ...w, quiet_hours: { ...q, start: e.target.value } })} />
        </label>
        <label className="field">
          <span>End (HH:MM)</span>
          <input type="time" value={q.end} disabled={!q.enabled} onChange={(e) => setW({ ...w, quiet_hours: { ...q, end: e.target.value } })} />
        </label>
        <div className="span2 row gap center">
          <button type="submit" className="btn primary small" disabled={!dirty || busy}>
            Save worker settings
          </button>
          {ok && !dirty && <span className="small ok-text">{ok}</span>}
        </div>
      </form>
      <ErrorMsg>{error}</ErrorMsg>
    </Section>
  );
}

function MapForm({ value }: { value: SettingsValues['map'] }) {
  const [m, setM] = useState(value);
  const { busy, error, ok, save } = useSave('map');
  useEffect(() => setM(value), [value]);
  const dirty = m.tile_url !== value.tile_url || m.tile_attribution !== value.tile_attribution;
  return (
    <Section title="Map tiles">
      <form
        className="stack"
        aria-label="Map tiles"
        onSubmit={(e) => {
          e.preventDefault();
          void save(m);
        }}
      >
        <label className="field">
          <span>Tile URL template</span>
          <input value={m.tile_url} onChange={(e) => setM({ ...m, tile_url: e.target.value })} className="mono" />
        </label>
        <label className="field">
          <span>Tile attribution (shown on the map)</span>
          <input value={m.tile_attribution} onChange={(e) => setM({ ...m, tile_attribution: e.target.value })} />
        </label>
        <div className="row gap center">
          <button type="submit" className="btn primary small" disabled={!dirty || busy}>
            Save
          </button>
          {ok && !dirty && <span className="small ok-text">{ok}</span>}
        </div>
      </form>
      <ErrorMsg>{error}</ErrorMsg>
      <p className="muted small">If tiles fail to load, the map still shows the markers and the list keeps working.</p>
    </Section>
  );
}

const GROUPS: [string, string][] = [
  ['directory', 'Directory'],
  ['jobs-reports', 'Jobs and reports'],
  ['models-agents', 'Models and agents'],
  ['outreach', 'Outreach'],
  ['display-data', 'Display and data'],
];

function SettingsGroup({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="settings-group" id={id} aria-labelledby={`${id}-title`}>
      <h2 className="group-title" id={`${id}-title`}>
        {title}
      </h2>
      <div className="settings-grid">{children}</div>
    </section>
  );
}

export function SettingsPage() {
  const { settings, settingsError, user } = useSession();
  if (!settings) return settingsError ? <ErrorMsg>{settingsError}</ErrorMsg> : <Loading what="Loading settings" />;
  const v = settings.values;
  return (
    <div className="page">
      <PageHead title="Settings" meta={user?.role === 'owner' ? 'You are the owner' : 'Read only for editors (except pausing the worker)'} />
      <nav className="jump" aria-label="Settings groups">
        {GROUPS.map(([id, label]) => (
          <a key={id} href={`#${id}`}>
            {label}
          </a>
        ))}
      </nav>
      <SettingsGroup id="directory" title="Directory">
        <ListEditor k="team_size_bands" title="Team-size bands" help="Bands offered when recording team size and in the Competitors filter. “unknown” is always available." values={v.team_size_bands} />
        <ListEditor k="service_categories" title="Service categories" help="Services a studio can be tagged with; website collection matches these." values={v.service_categories} />
        <ListEditor k="client_categories" title="Client categories" help="Kinds of clients a studio serves." values={v.client_categories} />
        <MirageProfileForm value={v.mirage_profile} services={v.service_categories} clients={v.client_categories} />
      </SettingsGroup>
      <SettingsGroup id="jobs-reports" title="Jobs and reports">
        <WorkerForm value={v.worker} />
        <ReportsForm value={v.reports} />
        <Section title="Reporting">
          <KV
            rows={[
              ['Time zone', v.reporting_timezone],
              ['Comparison currency', v.comparison_currency],
              ['Light collection', `${v.collection.max_pages_light} pages per studio`],
              ['Deep collection', `${v.collection.max_pages_deep} pages per run (default)`],
              ['Deep defaults', `every ${v.deep_defaults.cadence_days} days, ${v.deep_defaults.lookback_days} days lookback, channels: ${v.deep_defaults.channels.join(', ')}`],
            ]}
          />
          <p className="muted small">Every date in the app is shown in this time zone. Quiet hours use it too.</p>
        </Section>
      </SettingsGroup>
      <SettingsGroup id="models-agents" title="Models and agents">
        <ModelPolicyForm value={v.model_policy} note={settings.notes.model_policy} models={settings.claude_models} tasks={settings.agent_tasks} />
        {user?.role === 'owner' && <AgentTokens />}
        <OpenClawForm value={v.openclaw} />
      </SettingsGroup>
      <SettingsGroup id="outreach" title="Outreach">
        <Suppressions />
      </SettingsGroup>
      <SettingsGroup id="display-data" title="Display and data">
        <MapForm value={v.map} />
        <Section title="Demo data">
          <p className="small">
            Demo studios are fictional records on reserved <span className="mono">.example</span> domains, marked DEMO everywhere and counted
            apart from real studios. They are managed from the server command line, not from this page:
          </p>
          <pre className="cmd">uv run python -m app.cli seed-demo{'\n'}uv run python -m app.cli purge-demo</pre>
          <p className="muted small">Purging deletes the demo studios and everything attached to them (and demo batches); real studios are untouched.</p>
        </Section>
      </SettingsGroup>
    </div>
  );
}
